import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiArrowRight, FiCheckCircle, FiRepeat, FiX } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

// Cash-holder transfers — see finance/cash_transfer.py and
// brainstorms/2026-09-22-cash-holder-tracking-discovery.md. Deliberately
// separate from Quick Entry: both sides of a transfer are cash accounts
// (Cash & Bank Operating and/or a named person), never a revenue/expense
// category, so it doesn't fit Quick Entry's in/out + category shape.
const CURRENCIES = ['USD', 'TRY', 'ZAR', 'EUR', 'GBP'];

const TransferCash = () => {
  const [entities, setEntities] = useState([]);
  const [entityCode, setEntityCode] = useState('');
  const [holders, setHolders] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [sourceCode, setSourceCode] = useState('');
  const [destinationCode, setDestinationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    financeAPI.entities.list().then((res) => {
      const list = res.data.results || res.data || [];
      setEntities(list);
      if (list.length && !entityCode) setEntityCode(list[0].code);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHolders = useCallback(() => {
    if (!entityCode) return;
    financeAPI.entries.cashHolders(entityCode)
      .then((res) => setHolders(res.data || []))
      .catch(() => setHolders([]));
  }, [entityCode]);

  useEffect(() => { loadHolders(); }, [loadHolders]);

  const sourceHolder = useMemo(() => holders.find((h) => h.code === sourceCode), [holders, sourceCode]);
  // A holder's balance only means something per currency — 9,000 ZAR and
  // -14,000 TRY don't add up to anything — so compare in the one being moved.
  const sourceBalance = sourceHolder ? parseFloat((sourceHolder.balances || {})[currencyCode] || 0) : 0;
  const destinationOptions = useMemo(() => holders.filter((h) => h.code !== sourceCode), [holders, sourceCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    // Soft warning, not a hard block (2026-09-22 discovery decision) — every
    // holder's tracked balance starts as a fiction until real transfers
    // exist, so blocking on it would make entering the first real ones
    // impossible. Just confirm before submitting, computed from the
    // balances we already have on hand.
    if (sourceHolder && amount) {
      const projected = sourceBalance - parseFloat(amount);
      if (projected < 0) {
        const proceed = window.confirm(
          `This would leave ${sourceHolder.name} holding ${fs.money(projected, currencyCode)} — continue anyway?`
        );
        if (!proceed) return;
      }
    }

    setSubmitting(true);
    try {
      const res = await financeAPI.entries.transferCash({
        entity_code: entityCode, date, description, amount, currency_code: currencyCode,
        source_account_code: sourceCode, destination_account_code: destinationCode,
      });
      setResult(res.data);
      setDescription(''); setAmount(''); setSourceCode(''); setDestinationCode('');
      loadHolders();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save this transfer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiRepeat size={20} />Transfer Cash</h1>
          <p style={fs.pageSubtitle}>
            Record cash moving between holders — Cash &amp; Bank Operating, or directly between people.
          </p>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {result && (
        <div style={{ ...fs.card, borderColor: 'rgba(67,233,123,0.35)', background: 'rgba(67,233,123,0.06)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <FiCheckCircle size={20} color={fs.colors.green} />
          <div>
            <div style={{ color: fs.colors.textBright, fontWeight: 600 }}>
              {result.posted ? 'Transfer saved and posted.' : 'Transfer saved as a draft.'}
            </div>
            {result.note && <div style={{ color: fs.colors.textDim, fontSize: 13, marginTop: 2 }}>{result.note}</div>}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ ...fs.card, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={fs.label}>Entity</label>
          <select value={entityCode} onChange={(e) => setEntityCode(e.target.value)} style={{ ...fs.select, width: '100%' }} required>
            {entities.map((ent) => <option key={ent.code} value={ent.code}>{ent.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={fs.label}>From</label>
            <select value={sourceCode} onChange={(e) => setSourceCode(e.target.value)} style={{ ...fs.select, width: '100%' }} required>
              <option value="">Choose a holder…</option>
              {holders.map((h) => <option key={h.code} value={h.code}>{h.name}</option>)}
            </select>
            {sourceHolder && (
              <div style={{ fontSize: 12, color: fs.colors.textFaint, marginTop: 6 }}>
                currently holding {fs.money(sourceBalance, currencyCode)}
              </div>
            )}
          </div>
          <FiArrowRight size={18} color={fs.colors.textFaint} style={{ marginBottom: 10 }} />
          <div style={{ flex: 1 }}>
            <label style={fs.label}>To</label>
            <select value={destinationCode} onChange={(e) => setDestinationCode(e.target.value)} style={{ ...fs.select, width: '100%' }} required>
              <option value="">Choose a holder…</option>
              {destinationOptions.map((h) => <option key={h.code} value={h.code}>{h.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={fs.label}>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...fs.input, width: '100%' }} required />
        </div>

        <div>
          <label style={fs.label}>Description (optional)</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Float for the week"
            style={{ ...fs.input, width: '100%' }} />
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={fs.label}>Amount</label>
            <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              style={{ ...fs.input, width: '100%' }} required />
          </div>
          <div style={{ width: 120 }}>
            <label style={fs.label}>Currency</label>
            <select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} style={{ ...fs.select, width: '100%' }}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <button type="submit" disabled={submitting} style={{ ...fs.primaryButton, justifyContent: 'center' }}>
          {submitting ? 'Saving…' : 'Save transfer'}
        </button>
      </form>
    </div>
  );
};

export default TransferCash;
